import {
  isMultileg,
  MarketAccountTypeEnum,
  useMarketAccountsContext,
  useMarketsContext,
  useSecurity,
  useUnifiedLiquidity,
  type Market,
  type MarketAccount,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { usePermittedTradableMarketAccountsSet } from '../../../hooks/usePermittedTradableMarketAccountsSet';
import { useSubAccountAllocationsForRestrictedTrading } from '../../../hooks/useSubAccountAllocationsForRestrictedTrading';

export type UseMarketDataMarketsAndAccountsParams = { symbol: string | undefined } & Pick<
  GetMarketDataMarketsAndAccountsParams,
  'includeUnifiedLiquidityMarkets'
>;

/** Hook wrapper version of `getMarketDataMarketsAndAccounts` */
export const useMarketDataMarketsAndAccounts = ({
  symbol,
  includeUnifiedLiquidityMarkets,
}: UseMarketDataMarketsAndAccountsParams) => {
  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const security = useSecurity(symbol);
  const ulResponse = useUnifiedLiquidity(includeUnifiedLiquidityMarkets ? symbol : undefined);
  const { enableAccountSegregationWithRestrictedTrading } = useFeatureFlag();
  const subAccountAllocations = useSubAccountAllocationsForRestrictedTrading();

  const permittedSet = usePermittedTradableMarketAccountsSet({
    subAccountAllocations,
    combineAllMarketAccounts: enableAccountSegregationWithRestrictedTrading,
  });

  const permittedMarketAccountNames = enableAccountSegregationWithRestrictedTrading ? permittedSet : null;

  return useMemo(
    () =>
      getMarketDataMarketsAndAccounts({
        security,
        includeUnifiedLiquidityMarkets,
        ulResponse,
        marketAccountsByMarket,
        marketsByName,
        permittedMarketAccountNames,
      }),
    [
      security,
      includeUnifiedLiquidityMarkets,
      ulResponse,
      marketAccountsByMarket,
      marketsByName,
      permittedMarketAccountNames,
    ]
  );
};

export interface GetMarketDataMarketsAndAccountsParams {
  /**
   * The Security.Markets array will be used internally.
   * Security is required but we allow it to be undefined for ease-of-use. If undefined, the hook returns empty arrays.
   */
  security: Security | undefined;
  /** Whether or not Unified Liquidity should be used (when applicable) to collect Markets and Accounts. */
  includeUnifiedLiquidityMarkets?: boolean;
  /** The UL details for the security (if applicable) */
  ulResponse?: UnifiedLiquidityResponse;
  marketAccountsByMarket: Map<string, MarketAccount[]>;
  marketsByName: Map<string, Market>;
  /**
   * When provided and not null, market accounts are filtered to only those in this set (restricted trading mode).
   * When filtering, markets are returned as [] since accounts replace their parent markets.
   */
  permittedMarketAccountNames?: Set<string> | null;
}

/**
 * The purpose of this function is to collect the markets and accounts which are eligible to be viewed in contexts such as
 * the Market Data card settings.
 *
 * The function grabs the resolved Security.Markets and flattens into Market Accounts where possible. It removes any Markets which
 * also have found eligible Market Accounts (effectively replacing the Market with its Market Accounts).
 *
 * The function does not handle multileg symbols. The Multileg case is handled separately.
 */
export function getMarketDataMarketsAndAccounts({
  security,
  includeUnifiedLiquidityMarkets,
  ulResponse,
  marketAccountsByMarket,
  marketsByName,
  permittedMarketAccountNames,
}: GetMarketDataMarketsAndAccountsParams): { markets: Market[]; marketAccounts: MarketAccount[] } {
  if (!security || isMultileg(security)) {
    return { markets: [], marketAccounts: [] };
  }

  const securityMarkets = security.Markets ?? [];
  const maybeULMarkets = includeUnifiedLiquidityMarkets ? (ulResponse?.Tokens.map(token => token.Market) ?? []) : [];
  const allMarkets = compact(uniq([...securityMarkets, ...maybeULMarkets]).map(m => marketsByName.get(m)));

  const marketsWithoutAnyAccounts: Market[] = [];
  let marketAccounts: MarketAccount[] = [];

  for (const market of allMarkets) {
    const marketAccountsForMarket = marketAccountsByMarket
      .get(market.Name)
      // only trading accts
      ?.filter(mktAcc => mktAcc.Type === MarketAccountTypeEnum.Trading);

    // Perform the splitting here between markets and market accounts. Markets should only remain if they don't have any market accounts within them.
    if (marketAccountsForMarket && marketAccountsForMarket.length > 0) {
      marketAccounts.push(...marketAccountsForMarket);
    } else {
      marketsWithoutAnyAccounts.push(market);
    }
  }

  if (permittedMarketAccountNames != null) {
    marketAccounts = marketAccounts.filter(ma => permittedMarketAccountNames.has(ma.Name));
    return { markets: [], marketAccounts };
  }

  return { markets: marketsWithoutAnyAccounts, marketAccounts };
}
